import './styles.scss'

import { cloneElement, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Col, Form, Input, Row, Typography } from 'antd'
import type { FormInstance, FormItemProps } from 'antd'
import { clsx } from 'clsx'

import ShoppingCartType from '@/components/ShoppingCartType'
import UserStatusCheckbox from '@/pages/InviteNewUserPage/components/UserStatusCheckbox'
import {
  useEditUserComplete,
  useInviteNewUser,
} from '@/pages/UserManagementPage/hooks/useUserManagementMutations'
import { adminFapiUserManagement } from '@/services/admin'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectAssignableCountryRolesList } from '@/store/selectors/roleSelector'
import {
  selectAvailableLanguages,
  selectIsFetchingUserConfigurations,
  selectUserCountryId,
} from '@/store/selectors/userSelector'
import { selectUserId } from '@/store/slices/authenticationSlice'
import { customerActions } from '@/store/slices/customerSlice'
import { userActions } from '@/store/slices/userSlice'
import { useAssignRoles } from '@/hooks/useAssignRoles'
import usePermission from '@/hooks/usePermission'
import { USER_FORM_TYPES } from '@/libs/constants'
import { DEFAULT_LANGUAGES } from '@/libs/constants/lang'
import { MODULE_USER_MANAGEMENT_RIGHTS } from '@/libs/constants/modulerights'
import { USER_ACCOUNT_STATUS, USER_SALUTATION_ARRAY } from '@/libs/constants/user'
import { isValidArray } from '@/libs/utils/array'
import { runAsyncValidation, type ValidationCache } from '@/libs/utils/asyncValidation'
import { toUserStatusArray, toUserStatusString } from '@/libs/utils/userStatusUtils'
import type { CountryRole, CountryRoleList, InvitationRole, InvitationRolePayload } from '@/types/api/admin/roles.types'

import B2becAssignRole from '../B2becAssignRole'
import B2becCustomSelect from '../B2becCustomSelect'
import CustomButton from '../CustomButton'
import B2bSelectSalesOrg from './B2bSelectSalesOrg'

const { useForm } = Form

interface CustomFormItemProps extends Omit<FormItemProps, 'name' | 'label' | 'children'> {
  name: string
  children: React.ReactElement
  label?: string
  displayLabel?: boolean
  isSharedLabel?: boolean
}

export const CustomFormItem = ({
  className = undefined,
  name = '',
  children,
  label = '',
  displayLabel = true,
  isSharedLabel = true,
  ...restProps
}: CustomFormItemProps) => {
  const { t } = useTranslation()

  const formLabel = displayLabel
    ? isSharedLabel
      ? t(`form.label.${label || name}`)
      : t(`${label || name}`)
    : ''

  return (
    <Form.Item
      className={clsx('custom-form-item', className)}
      label={formLabel}
      name={name}
      {...restProps}
    >
      {cloneElement(children, {
        placeholder: t(`form.placeHolder.${label || name}`),
      })}
    </Form.Item>
  )
}

interface B2bUserFormProps {
  form?: FormInstance
  initialValues?: {
    contactEmail?: string
    sapCustomerNumber?: string
    isInvitation?: boolean
    countryRoles?: CountryRole[]
    language?: string
    userStatus?: string
    sessionBasedShoppingCart?: string
    salutation?: string
    firstName?: string
    lastName?: string
    contactNumber?: string
    countryId?: string
    title?: string
    email?: string
    phoneNumber?: string
    assignedRoles?: InvitationRole[]
  }
  submitFunc: (body: unknown) => Promise<unknown>
  submitText?: React.ReactNode
  errorMessageKey?: string
  successMessageKey?: string
  onSuccess?: (requestBody: unknown, form: unknown) => void
  onError?: (error: unknown, form: unknown) => void
  onCancel?: () => void
  type?: 'invite' | 'edit'
  isInvitation?: boolean
  userId?: string
  isPending?: boolean
}

const B2bUserForm = ({
  form: propForm,
  initialValues,
  submitFunc,
  submitText,
  errorMessageKey = 'userManagement.messages.createUserInvitationError',
  successMessageKey = 'userManagement.messages.createUserInvitationSuccess',
  onSuccess,
  onError,
  onCancel,
  type = 'invite',
  userId,
  isInvitation = false,
  isPending = false,
  ...props
}: B2bUserFormProps) => {
  const { language } = initialValues || {}

  const { t } = useTranslation()

  const [isError, setIsError] = useState(false)
  const [form] = useForm(propForm)

  const emailValidationCacheRef = useRef<ValidationCache>(null)
  const phoneValidationCacheRef = useRef<ValidationCache>(null)
  const emailRequestVersionRef = useRef(0)
  const phoneRequestVersionRef = useRef(0)

  // Clear caches whenever the form session changes so validation results do not leak
  // across reused modal instances with the same blank initial values.
  useEffect(() => {
    emailValidationCacheRef.current = null
    phoneValidationCacheRef.current = null
    emailRequestVersionRef.current = 0
    phoneRequestVersionRef.current = 0
  }, [form, initialValues, type, userId, isInvitation])

  const isEditForm = type === USER_FORM_TYPES.EDIT
  const isInvitationForm = type === USER_FORM_TYPES.INVITE

  const currentUserId = useAppSelector(selectUserId)
  const isFetchingUserConfigurations = useAppSelector(
    selectIsFetchingUserConfigurations,
  )
  const countryId = useAppSelector(selectUserCountryId)
  const availableLanguages = useAppSelector(selectAvailableLanguages)
  const assignableCountryRolesList = useAppSelector(
    selectAssignableCountryRolesList,
  )
  const { hasPermission: canUpdateUser } = usePermission(
    MODULE_USER_MANAGEMENT_RIGHTS.UPDATE_USER,
  )

  const shouldShowAssignRolesSection = canUpdateUser
    && isValidArray(assignableCountryRolesList)

  const cannotAssignRoles = isInvitationForm && !isValidArray(assignableCountryRolesList)

  const customInitialValues = useMemo(() => {
    if (isEditForm) {
      const formattedUserStatus = toUserStatusArray(initialValues?.userStatus)
      const matchedLang = availableLanguages.find((item) => item?.value === language)
      const resolvedLanguage = language && language !== 'N/A' && matchedLang?.label ? matchedLang.value : ''
      return { salutation: 'Other', ...initialValues, language: resolvedLanguage, countryId, userStatus: formattedUserStatus }
    }
    if (isInvitationForm) {
      return {
        salutation: 'Other',
        ...initialValues,
        userStatus: [USER_ACCOUNT_STATUS.AUTHORIZED],
        countryId,
      }
    }
    return undefined
  }, [isEditForm, isInvitationForm, initialValues, language, availableLanguages, countryId])

  const dispatch = useAppDispatch()

  const handleSuccess = (requestBody) => onSuccess?.(requestBody, form)

  const handleError = (error) => {
    setIsError(true)
    onError?.(error, form)
  }

  const onSubmit = async ({
    contactEmail: email,
    contactNumber: phoneNumber,
    sapCustomerNumber,
    userStatus,
    salutation: title,
    countryRoles,
    language: formLanguage,
    ...values
  }) => {
    const requestBody = {
      email,
      phoneNumber,
      title: title === 'Other' ? 'None' : title,
      language: formLanguage,
      status: toUserStatusString(userStatus),
      sapCustomerNumber,
      ...values,
    }

    setIsError(false)

    if (isEditForm) {
      const resolvedLanguage = formLanguage
        || (language && language !== 'N/A' ? language : DEFAULT_LANGUAGES[0].value)
      const finalRequestBody = { ...requestBody, language: resolvedLanguage }

      const hasRolesChanged = JSON.stringify(countryRoles)
        !== JSON.stringify(initialValues?.countryRoles)

      // For invitations: always send roles with name
      // For users: only send roles when changed (uses a separate assignRolesToUser mutation)
      const roleIds = (isInvitation || hasRolesChanged)
        ? buildRolesWithNames(countryRoles ?? [], assignableCountryRolesList)
        : undefined

      try {
        await submitFunc({
          userId,
          data: {
            ...finalRequestBody,
            roleIds,
          },
          isInvitation,
          hasEmailChanged: email !== initialValues?.contactEmail,
          hasSapChanged: sapCustomerNumber !== initialValues?.sapCustomerNumber,
          hasRolesChanged,
          onSuccess: () => {
            if (
              currentUserId === userId
              && sapCustomerNumber !== initialValues?.sapCustomerNumber
            ) {
              dispatch(customerActions.updateCustomerSAP(sapCustomerNumber))
            }

            if (userId === currentUserId) {
              dispatch(
                userActions.getUserDetails({
                  userId: currentUserId,
                }),
              )
            }

            handleSuccess(finalRequestBody)
          },
        })
      } catch (error) {
        handleError(error)
      }
    } else {
      try {
        await submitFunc({ ...requestBody, countryRoles })
        handleSuccess(requestBody)
      } catch (error) {
        handleError(error)
      }
    }
  }

  const validateMessages = {
    required: (label = '') => t('form.validate.required', { label }),
    types: {
      email: (label = '') => t('form.validate.isNotValid', { label }),
    },
  }

  return (
    <>
      {isError
        ? (
          <Typography.Text
            className="b2bec-change-invoice__message"
            type="danger"
          >
            {t(errorMessageKey)}
          </Typography.Text>
        )
        : null}
      <Form
        key={countryId}
        className="b2b-user-form"
        form={form}
        initialValues={customInitialValues}
        layout="vertical"
        validateMessages={validateMessages}
        onFinish={onSubmit}
        scrollToFirstError={{ behavior: 'smooth', block: 'center', scrollMode: 'always' }}
        {...props}
      >
        <Row gutter={24}>
          <Col span={12}>
            <CustomFormItem name="salutation">
              <B2becCustomSelect
                options={USER_SALUTATION_ARRAY.map((item) => ({
                  label: t(`form.label.${item.label}`),
                  value: item.value,
                }))}
              />
            </CustomFormItem>
          </Col>
          <Col span={12} />
          <Col span={12}>
            <CustomFormItem
              name="firstName"
              rules={[{ required: !isInvitationForm }]}
            >
              <Input />
            </CustomFormItem>
          </Col>
          <Col span={12}>
            <CustomFormItem
              name="lastName"
              rules={[{ required: !isInvitationForm }]}
            >
              <Input />
            </CustomFormItem>
          </Col>
          <Col span={24}>
            <CustomFormItem
              name="contactEmail"
              validateTrigger="onBlur"
              rules={[
                { required: true },
                { type: 'email' },
                {
                  validator: async (_, value) => {
                    if (!value?.trim()) return
                    if (isEditForm && value === initialValues?.contactEmail) return
                    return runAsyncValidation({
                      cacheRef: emailValidationCacheRef,
                      versionRef: emailRequestVersionRef,
                      callApi: async (value) => adminFapiUserManagement.checkEmailExists(value),
                      isResponseInvalid: (response) => Boolean(response?.data),
                      errorMessage: t('form.validate.emailInUse'),
                    }, value)
                  },
                },
              ]}
            >
              <Input disabled={isInvitation} />
            </CustomFormItem>
          </Col>
          <Col span={isEditForm ? 24 : 12}>
            <CustomFormItem
              name="contactNumber"
              validateTrigger="onBlur"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value?.trim()) return
                    if (isEditForm && value === initialValues?.contactNumber) return
                    return runAsyncValidation({
                      cacheRef: phoneValidationCacheRef,
                      versionRef: phoneRequestVersionRef,
                      callApi: adminFapiUserManagement.validatePhoneNumber,
                      isResponseInvalid: (response) => response?.data === false,
                      errorMessage: t('form.validate.isNotValid', { label: 'contactNumber' }),
                    }, value)
                  },
                },
              ]}
            >
              <Input />
            </CustomFormItem>
          </Col>
          <Col span={12}>
            <CustomFormItem
              name="sapCustomerNumber"
              rules={[{ required: true }]}
            >
              <Input />
            </CustomFormItem>
          </Col>
          <Col span={12}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.salesOrg')}
              name="countryId"
              rules={[
                {
                  required: true,
                  message: t('form.validate.required', {
                    label: 'salesOrg',
                  }),
                },
              ]}
            >
              <B2bSelectSalesOrg disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <CustomFormItem
              name="language"
              rules={[{ required: isInvitationForm }]}
            >
              <B2becCustomSelect
                disabled={isFetchingUserConfigurations}
                loading={isFetchingUserConfigurations}
                options={availableLanguages}
              />
            </CustomFormItem>
          </Col>
          {shouldShowAssignRolesSection && <B2becAssignRole />}
          <Col span={24}>
            <CustomFormItem name="userStatus" rules={[{ required: true }]}>
              <UserStatusCheckbox isDisabled={isInvitationForm} />
            </CustomFormItem>
          </Col>
          <Col span={24}>
            <CustomFormItem name="sessionBasedShoppingCart">
              <ShoppingCartType isDisabled />
            </CustomFormItem>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col>
            <Form.Item noStyle>
              <CustomButton
                type="ghost"
                disabled={isPending}
                onClick={onCancel}
              >
                {t('buttonTexts.cancel')}
              </CustomButton>
            </Form.Item>
          </Col>
          <Col>
            <Form.Item noStyle>
              <CustomButton htmlType="submit" disabled={isPending || cannotAssignRoles}>
                {submitText}
              </CustomButton>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  )
}

function buildRolesWithNames(
  countryRoles: CountryRole[],
  assignableCountryRolesList: CountryRoleList[] | undefined,
): InvitationRolePayload[] {
  return countryRoles.flatMap(({ salesOrg, roles: roleIds = [] }) =>
    roleIds.map((roleId) => {
      const countryEntry = assignableCountryRolesList?.find((item) => item.country === salesOrg)
      const roleOption = countryEntry?.roles?.find((r) => r.value === roleId)
      return { country: salesOrg, role: roleId, name: roleOption?.label ?? roleId }
    }),
  )
}

interface B2bUserInvitationFormProps extends Omit<
  B2bUserFormProps,
  'form' | 'submitFunc' | 'submitText' | 'errorMessageKey' | 'successMessageKey' | 'type' | 'isPending'
> {
  initialValues?: B2bUserFormProps['initialValues']
}

export const B2bUserInvitationForm = ({
  initialValues: externalInitialValues,
  ...props
}: B2bUserInvitationFormProps) => {
  const inviteUserMutation = useInviteNewUser()
  const [form] = useForm()
  const { countryRoles } = useAssignRoles()
  const assignableCountryRolesList = useAppSelector(selectAssignableCountryRolesList)
  const hasAppliedInitialRolesRef = useRef(false)

  // Form.initialValues is only applied on mount. If assignableCountries loads
  // after the form mounts (race condition on direct URL access), countryRoles starts as []
  // and never gets applied. Imperatively set once when countryRoles first becomes non-empty.
  useEffect(() => {
    if (hasAppliedInitialRolesRef.current || !isValidArray(countryRoles)) return
    hasAppliedInitialRolesRef.current = true
    if (!isValidArray(form.getFieldValue('countryRoles'))) {
      form.setFieldValue('countryRoles', countryRoles)
    }
  }, [form, countryRoles])

  const handleSubmit = async (body) => {
    const { countryRoles, ...restBody } = body ?? {}
    const roles = buildRolesWithNames(countryRoles ?? [], assignableCountryRolesList)

    return inviteUserMutation.mutateAsync({
      ...restBody,
      customerNumber: restBody?.sapCustomerNumber,
      salutation: restBody?.title,
      roles,
    })
  }

  return (
    <B2bUserForm
      {...props}
      form={form}
      errorMessageKey="userManagement.messages.editUserError"
      submitFunc={handleSubmit}
      successMessageKey="userManagement.messages.createUserInvitationSuccess"
      type={USER_FORM_TYPES.INVITE}
      submitText={<Trans i18nKey="buttonTexts.inviteNewUser" />}
      isPending={inviteUserMutation.isPending}
      initialValues={{ ...externalInitialValues, countryRoles }}
    />
  )
}

const convertAssignedRolesToCountryRoles = (
  assignedRoles: InvitationRole[] | undefined,
  countryId: string | undefined,
): CountryRole[] => {
  if (!countryId) {
    return []
  }

  if (!isValidArray(assignedRoles)) {
    return [{ salesOrg: countryId, roles: [] }]
  }

  const roleIds = assignedRoles.map((role) => role.roleId).filter(Boolean)
  return [{ salesOrg: countryId, roles: roleIds }]
}

/**
 * For invitations: converts assignedRoles (display format from API)
 * to countryRoles (form format)
 *
 * For regular users: sets countryRoles to empty array
 * (actual countryRoles are provided separately via reduxCountryRoles)
 */
interface FormatValuesInput {
  firstName?: string
  lastName?: string
  language?: string
  countryId?: string
  email?: string
  phoneNumber?: string
  userStatus?: string
  salutation?: string
  isInvitation?: boolean
  sapCustomerNumber?: string
  assignedRoles?: InvitationRole[]
  sessionBasedShoppingCart?: string
}

interface FormatValuesOutput {
  firstName?: string
  lastName?: string
  contactEmail?: string
  contactNumber: string
  language?: string
  countryId?: string
  userStatus?: string
  salutation?: string
  isInvitation?: boolean
  sapCustomerNumber?: string
  countryRoles: CountryRole[]
  sessionBasedShoppingCart?: string
}

const formatValues = ({
  firstName,
  lastName,
  language,
  countryId,
  email: contactEmail,
  phoneNumber,
  userStatus,
  salutation,
  isInvitation,
  sapCustomerNumber,
  assignedRoles,
  sessionBasedShoppingCart,
}: FormatValuesInput): FormatValuesOutput => {
  const countryRoles = isInvitation
    ? convertAssignedRolesToCountryRoles(assignedRoles, countryId)
    : []

  return {
    firstName,
    lastName,
    contactEmail,
    contactNumber: phoneNumber !== 'N/A' && phoneNumber ? phoneNumber : '',
    language,
    countryId,
    userStatus,
    salutation,
    isInvitation,
    sapCustomerNumber,
    countryRoles,
    sessionBasedShoppingCart,
  }
}

interface B2bUserEditFormProps {
  userId: string
  initialValues: {
    firstName?: string
    lastName?: string
    contactEmail?: string
    contactNumber?: string
    language?: string
    countryId?: string
    userStatus?: string
    salutation?: string
    sapCustomerNumber?: string
    countryRoles?: CountryRole[]
    sessionBasedShoppingCart?: string
    title?: string
    email?: string
    phoneNumber?: string
    assignedRoles?: InvitationRole[]
  }
  isInvitation?: boolean
  [key: string]: unknown
}

/**
 * Edit form wrapper for both users and invitations
 *
 * Role handling differs:
 * - Invitations: roles come from API (via initialValues.assignedRoles)
 *   Formatted to countryRoles by formatValues()
 *
 * - Users: roles come from Redux store via useAssignRoles()
 *   Already in countryRoles format
 *
 * This ensures both UIs use the most current role data for their context
 */
export const B2bUserEditForm = ({
  userId,
  initialValues,
  isInvitation,
  ...props
}: B2bUserEditFormProps) => {
  const values = formatValues(initialValues)
  const { countryRoles: reduxCountryRoles } = useAssignRoles(userId)
  const editUserCompleteMutation = useEditUserComplete()

  const handleSubmit = ({
    userId,
    data,
    isInvitation,
    hasEmailChanged,
    hasSapChanged,
    hasRolesChanged,
    onSuccess,
  }) => {
    editUserCompleteMutation.mutate({
      userId,
      data,
      isInvitation,
      hasEmailChanged,
      hasSapChanged,
      hasRolesChanged,
      onSuccess,
    })
  }

  // For invitations, use countryRoles from API (via formatValues)
  // For users, use countryRoles from Redux (synced via mutations)
  const countryRoles = isInvitation ? values.countryRoles : reduxCountryRoles

  return (
    <B2bUserForm
      {...props}
      errorMessageKey="userManagement.messages.editUserError"
      isInvitation={isInvitation}
      submitFunc={handleSubmit}
      submitText={<Trans i18nKey="buttonTexts.saveChanges" />}
      successMessageKey="userManagement.messages.editUserSuccess"
      type={USER_FORM_TYPES.EDIT}
      userId={userId}
      isPending={editUserCompleteMutation.isPending}
      initialValues={{
        ...values,
        countryRoles,
      }}
    />
  )
}
